import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {MethodExecutionStatus} from './getStudyDesignFromProcedureRun';

// These are the two terminal statuses that allow advancing to the next unit op
export const ADVANCING_PROCESS_TASK_STATUS_TYPES = new Set<ProcessTaskStatusType>([
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.SKIPPED,
]);
const TERMINAL_PROCESS_TASK_STATUS_TYPES = new Set<ProcessTaskStatusType>([
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.SKIPPED,
  ProcessTaskStatusType.INVALID,
  ProcessTaskStatusType.FAILED,
  ProcessTaskStatusType.CANCELLED,
]);

export interface MethodIdAndName {
  id: string;
  name: string;
}

function areAllStatusesPendingOrMissing(statuses: Array<ProcessTaskStatusType | null>): boolean {
  // TODO (BNCH-156724): Remove PENDING option once delayed mapping work is complete
  return statuses.every((status) => status == null || status === ProcessTaskStatusType.PENDING);
}

function canAdvanceFromPreviousMethod(statuses: Array<ProcessTaskStatusType | null>): boolean {
  if (statuses.length === 0) {
    return false;
  }

  const allTerminal = statuses.every(
    (status) => status != null && TERMINAL_PROCESS_TASK_STATUS_TYPES.has(status)
  );
  const hasAdvancingStatus = statuses.some(
    (status) => status != null && ADVANCING_PROCESS_TASK_STATUS_TYPES.has(status)
  );
  return allTerminal && hasAdvancingStatus;
}

/**
 * Returns the active method by scanning methods in flowchart order.
 *
 * A method is active when all of its PMEI method-task statuses are either missing (`null`)
 * or `PENDING`, and if it is not the first method, the immediately previous method's PMEI
 * statuses are all terminal with at least one advancing status (`COMPLETED` or `SKIPPED`).
 */
export function getActiveMethod(
  methods: Array<MethodIdAndName>,
  methodExecutionStatuses: Array<MethodExecutionStatus>
): MethodIdAndName | null {
  const statusesByMethodId = methodExecutionStatuses.reduce((statusesById, instance) => {
    statusesById.set(instance.methodId, [...(statusesById.get(instance.methodId) ?? []), instance.status]);
    return statusesById;
  }, new Map<string, Array<ProcessTaskStatusType | null>>());

  for (let methodIndex = 0; methodIndex < methods.length; methodIndex++) {
    const method = methods[methodIndex];
    const methodStatuses = statusesByMethodId.get(method.id) ?? [];
    if (!areAllStatusesPendingOrMissing(methodStatuses)) {
      continue;
    }

    if (methodIndex === 0) {
      return method;
    }

    const previousMethod = methods[methodIndex - 1];
    const previousMethodStatuses = statusesByMethodId.get(previousMethod.id) ?? [];
    if (canAdvanceFromPreviousMethod(previousMethodStatuses)) {
      return method;
    }
  }

  // No active method
  return null;
}
